// ─────────────────────────────────────────────────────────────────────────────
// A régua da ociosidade — a parte que é só conta.
//
// O monitor vive em services/useAtividade.js, cercado de React: refs, intervalo
// e chamadas de rede. A REGRA, que é o que erra na prática, mora aqui: funções
// puras, sem fetch e sem hook, para poder ser conferida sem subir a aplicação
// (scripts/teste-ociosidade.mjs).
//
// ─────────────────────────────────────────────────────────────────────────────
// POR QUE A ÂNCORA NÃO PODE ANDAR PARA TRÁS
//
// O cronômetro do ócio não parte de "agora": ele parte da ÚLTIMA VEZ que o
// operador tocou em um ticket. Esse instante — a âncora — vem de duas origens
// que não andam no mesmo passo:
//
//   · o MySuite, consultado a cada poucos minutos. É a verdade, mas CHEGA
//     ATRASADA: a alteração leva minutos para aparecer na API;
//   · o próprio Orion, quando o operador está escrevendo o atendimento. É
//     imediato, mas o MySuite ainda não sabe dele.
//
// Antes, cada consulta SOBRESCREVIA a âncora com o que o MySuite dissesse. O
// resultado era um relógio que andava para trás:
//
//   09:00  o operador começa a escrever         âncora = 09:00 (Orion)
//   09:05  envia, e o ticket sai do status 36   não há mais atendimento aberto
//   09:07  consulta: o MySuite ainda mostra a
//          alteração das 08:20                  âncora = 08:20  ← andou para trás
//   09:10  conta: 50 minutos sem atender        OCIOSIDADE, dois minutos depois
//                                               de concluir um atendimento
//
// Daí a regra: a âncora só AVANÇA. O instante mais recente entre o que já se
// sabia e o que o MySuite trouxe — nunca o mais antigo.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A âncora depois de uma consulta ao MySuite.
 *
 * @param {number|null} anterior  âncora atual, em ms (null = ainda não há)
 * @param {number|null} marca     última alteração vinda do MySuite, em ms
 * @returns {{ ancora: number|null, houveInteracaoNova: boolean }}
 *
 * `houveInteracaoNova` é o que devolve o operador para Ativo sozinho, e por
 * isso só é verdade quando a marca é MAIS RECENTE que a âncora — uma marca
 * antiga chegando atrasada não é trabalho novo.
 */
export function proximaAncora(anterior, marca) {
  const a = Number.isFinite(anterior) ? anterior : null;
  const m = Number.isFinite(marca) ? marca : null;

  if (m === null) return { ancora: a, houveInteracaoNova: false };
  if (a === null) return { ancora: m, houveInteracaoNova: true };

  return { ancora: Math.max(a, m), houveInteracaoNova: m > a };
}

/**
 * Minutos sem atender, a partir da âncora.
 *
 * `folgaMin` desconta o tempo que a alteração leva para aparecer na API: sem
 * ela, quem acabou de encerrar um ticket já entraria no cronômetro com alguns
 * minutos de atraso embutidos.
 *
 * Devolve `null` quando não há âncora — "não sei" não é "está ocioso desde a
 * meia-noite".
 */
export function minutosSemAtender(ancora, agora, folgaMin = 0) {
  if (!Number.isFinite(ancora)) return null;
  return Math.max(0, (agora - ancora) / 60000 - folgaMin);
}

// ─────────────────────────────────────────────────────────────────────────────
// AS DUAS FONTES DO "ESTÁ ATENDENDO?"
//
// Foi medido contra a API do MySuite, no mesmo instante, e as duas divergem —
// cada uma para um lado:
//
//   · `/tickets/filtro` com status 36 é a leitura mais fresca, mas não vê
//     tudo: havia ticket em "SUP - Em Atendimento" que só ele trazia;
//   · a fila de pendentes vê tickets que o filtro não trouxe, mas ATRASA:
//     devolveu como 36 um ticket concluído havia nove minutos.
//
// Acreditar em uma só é escolher qual erro cometer. Aqui vale o OU — e para a
// ociosidade esse é o lado certo de errar: um falso "está atendendo" custa
// alguns minutos a menos de ócio apurado; um falso "não está" acusa de parado
// quem estava trabalhando, e isso vai para o relatório do operador.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {number|null} quantosNoFiltro  tickets em status 36 (null = falhou)
 * @param {Array|null}  pendentes        tickets 36/setor 3 do operador na fila
 *                                       de pendentes (null = falhou)
 * @returns {boolean|null} `null` quando NENHUMA das duas respondeu.
 */
export function decidirEmAtendimento(quantosNoFiltro, pendentes) {
  const temFiltro = Number.isFinite(quantosNoFiltro);
  const temPendentes = Array.isArray(pendentes);

  if (!temFiltro && !temPendentes) return null;

  return (temFiltro && quantosNoFiltro > 0) || (temPendentes && pendentes.length > 0);
}

/**
 * Fuso em que o MySuite grava data e hora: sempre o horário da empresa.
 *
 * Fixo de propósito. O fuso da máquina que roda o Orion não tem relação nenhuma
 * com o fuso em que a alteração foi gravada — e `new Date('2026-08-26T10:58:00')`
 * usa o da máquina. No Electron do operador dá certo por coincidência; na
 * versão web servida pelo container (UTC), ou num computador com o fuso
 * trocado, a última alteração vai três horas para trás e a ociosidade dispara
 * na mesma hora.
 */
export const FUSO_MYSUITE = '-03:00';

/**
 * O instante de uma alteração do MySuite, ou `null` quando não dá para ler.
 *
 * Sem `horaAlteracao` o dia conta como meia-noite: só acontece em registro
 * antigo, e quem chama já limitou a consulta ao dia de hoje.
 */
export function instanteDaAlteracao(dataAlteracao, horaAlteracao) {
  const d = String(dataAlteracao ?? '').trim();
  if (!d) return null;
  const h = String(horaAlteracao ?? '').trim() || '00:00:00';

  const quando = new Date(`${d}T${h}${FUSO_MYSUITE}`);
  return Number.isNaN(quando.getTime()) ? null : quando;
}

/**
 * A alteração mais recente de uma lista de tickets, ou `null` quando não há
 * nenhuma legível.
 */
export function alteracaoMaisRecente(tickets = []) {
  let maior = null;
  (Array.isArray(tickets) ? tickets : []).forEach((t) => {
    const quando = instanteDaAlteracao(t?.dataAlteracao, t?.horaAlteracao);
    if (quando && (!maior || quando > maior)) maior = quando;
  });
  return maior;
}
